import { formatAmount } from "../../include/money";
import { useCallback, useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, headingCase, primaryButtonSx } from "./consoleTheme";
import { buildOrderTickets, saveTicketFile, shareTicketFile } from "../../include/ticketImage";
import { indiaDateTime } from "../../include/indiaDate";
import { LaunchChooser } from "./LaunchTimes";

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}

function ticketLink(ticketId) {
  return `${window.location.origin}/drawticket/${ticketId}`;
}

function issuedShareLink(token) {
  return `${window.location.origin}/ticketissued/${token}`;
}

function winnersShareLink(token) {
  return `${window.location.origin}/ticketissued/${token}?view=winners`;
}

function toLocal(value) {
  return String(value || "").replace(" ", "T").slice(0, 16);
}

function ticketWindowLabel(ticket) {
  const start = indiaDateTime(ticket?.start_at);
  const end = indiaDateTime(ticket?.end_at);
  if (start && end) return `${start} to ${end}`;
  if (end) return `Ends ${end}`;
  return "";
}

function sumQty(orders) {
  return (orders || []).reduce((sum, order) => sum + Number(order.qty || 0), 0);
}

export function issuedStatsLine(orders, ticket) {
  return `${orders.length} purchased · Total qty ${sumQty(orders)} · ${ticket?.winner_count || 0} winners · Number of scans ${ticket?.open_count || 0}`;
}

export function matchesCustomer(order, q) {
  const text = String(q || "").trim().toLowerCase();
  if (!text) return true;
  const name = String(order.customer_name || "").toLowerCase();
  const mobile = String(order.customer_mobile || "").replace(/\D/g, "");
  const digits = text.replace(/\D/g, "");
  if (name.includes(text)) return true;
  if (digits.length >= 3 && mobile.includes(digits)) return true;
  return false;
}

export function TicketSearchField({ value, onChange }) {
  return (
    <TextField
      size="small"
      fullWidth
      label="Search name or mobile"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ maxWidth: 420, "& .MuiInputBase-root": { background: "#fff" } }}
    />
  );
}

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [chooser, setChooser] = useState(null);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);

  const load = useCallback(async () => {
    const result = await adminPost("TICKET_LIST");
    if (result.status === "200") setTickets(result.data?.tickets || []);
    else toast.error(result?.status_message || "Could not load tickets");
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const text = query.trim();
    if (!text) {
      setHits([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      const result = await adminPost("TICKET_SEARCH", { q: text });
      if (result.status === "200") setHits(result.data?.orders || []);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const toggle = async (ticket) => {
    const next = ticket.status === "1" ? "0" : "1";
    const result = await adminPost("TICKET_STATUS", { ticket_id: ticket.ticket_id, status: next });
    if (result.status === "200") {
      toast.success(result.data?.message || (next === "1" ? "Ticket enabled" : "Ticket disabled"));
      load();
    } else toast.error(result?.data?.error || result?.status_message || "Could not update the ticket");
  };

  const totalTickets = tickets.length;
  const totalQty = tickets.reduce((sum, ticket) => sum + Number(ticket.sold || 0), 0);
  const totalScans = tickets.reduce((sum, ticket) => sum + Number(ticket.open_count || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Box>
          <Typography sx={{ color: consoleColors.ink, fontSize: 16, fontWeight: 750 }}>Total tickets {totalTickets} · Total qty {totalQty} · Number of scans {totalScans}</Typography>
          <Typography sx={{ color: consoleColors.muted, fontSize: 14, mt: 0.3 }}>Share the link or QR. Anyone who opens it can buy a Lucky Draw Ticket.</Typography>
        </Box>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/console/tickets/new")}><Add sx={{ mr: 0.5 }} /> Create Lucky Draw Ticket</Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TicketSearchField value={query} onChange={setQuery} />
      </Box>
      {query.trim() ? (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 750, mb: 1 }}>{hits.length} matching purchases</Typography>
          <IssuedCustomerTable ticket={null} orders={hits} emptyText="No matching name or mobile." />
        </Box>
      ) : null}
      {tickets.length === 0 && <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>No Lucky Draw Ticket yet.</Box>}
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {tickets.map((ticket) => (
          <Box key={ticket.ticket_id} sx={{ background: ticket.status === "1" ? "#fff" : "#f6eaea", border: `1px solid ${ticket.status === "1" ? consoleColors.line : "#e7cfcf"}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <QRCodeCanvas value={ticketLink(ticket.ticket_id)} size={84} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{headingCase(ticket.title)}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.ink, mt: 0.4 }}>{[ticketWindowLabel(ticket), `₹${formatAmount(ticket.price)} each`, `up to ${ticket.max_qty} coupons at a time`, `${ticket.open_count || 0} link opens`, `${ticket.sold || 0} purchased`, `${ticket.winner_count || 0} winners`, ticket.ended === "1" ? "Expired" : ticket.upcoming === "1" ? "Not started" : (ticket.status === "1" ? "Enabled" : "Disabled")].filter(Boolean).join(" · ")}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" sx={primaryButtonSx} onClick={() => setChooser(ticket)}>Launch draw</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/tickets/${ticket.ticket_id}/edit`)}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => toggle(ticket)}>{ticket.status === "1" ? "Disable" : "Enable"}</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/tickets/${ticket.ticket_id}/issued`)}>Issued</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/tickets/${ticket.ticket_id}/issued?view=winners`)}>Winners</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => { navigator.clipboard.writeText(ticketLink(ticket.ticket_id)); toast.success("Link copied"); }}>Copy buy link</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => { navigator.clipboard.writeText(issuedShareLink(ticket.list_token)); toast.success("Issued list link copied"); }}>Share issued list</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => { navigator.clipboard.writeText(winnersShareLink(ticket.list_token)); toast.success("Winners link copied"); }}>Share winners</Button>
            </Box>
          </Box>
        ))}
      </Box>
      <LaunchChooser
        open={Boolean(chooser)}
        title={chooser?.title || ""}
        launches={chooser?.launches || []}
        single
        onClose={() => setChooser(null)}
        onOpen={() => navigate(`/console/tickets/${chooser.ticket_id}/launch?slot=0`)}
        onShare={async () => {
          const result = await adminPost("LAUNCH_LINK", { kind: "ticket", ticket_id: chooser.ticket_id, result_mode: "manual", launch_index: 0 });
          if (result.status !== "200") {
            toast.info(result.status_message || result.data?.error || "Could not make the launch link");
            return "";
          }
          return `${window.location.origin}${result.data.path}`;
        }}
      />
    </Box>
  );
}

export function TicketForm() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", max_qty: "1", price: "", start_at: "", end_at: "", image: "", banner: "", banner_link: "", launch_at: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ticketId) return undefined;
    let mounted = true;
    adminPost("TICKET_DETAIL", { ticket_id: ticketId }).then((result) => {
      if (!mounted || result.status !== "200" || !result.data?.ticket) return;
      const ticket = result.data.ticket;
      setForm({
        title: ticket.title || "",
        max_qty: String(ticket.max_qty || 1),
        price: String(ticket.price || ""),
        start_at: toLocal(ticket.start_at),
        end_at: toLocal(ticket.end_at),
        image: ticket.image || "",
        banner: ticket.banner || "",
        banner_link: ticket.banner_link || "",
        launch_at: toLocal(ticket.launches?.[0]?.at),
      });
    });
    return () => { mounted = false; };
  }, [ticketId]);

  const onImage = (file, field = "image") => {
    if (!file) return;
    if (file.size > 900000) {
      toast.error(field === "banner" ? "Use a banner under 900 KB" : "Use an image under 900 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, [field]: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost("TICKET_SAVE", ticketId ? { ...form, ticket_id: ticketId } : form);
      if (result.status === "200") {
        toast.success(result.data?.message || "Ticket created");
        navigate(`/console/tickets/${result.data.ticket.ticket_id}`);
      } else toast.error(result?.data?.error || result?.status_message || "Could not save the ticket");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/tickets")}>Back to Lucky Draw Ticket</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{ticketId ? "Edit Lucky Draw Ticket" : "Create Lucky Draw Ticket"}</Typography>
        <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextField label="Coupons you can buy at a time" type="number" value={form.max_qty} onChange={(e) => setForm({ ...form, max_qty: e.target.value })} helperText="The most coupons one person can buy in a single payment." />
        <TextField label="Price per coupon" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
        <TextField label="Starts" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} InputLabelProps={{ shrink: true }} helperText="Buy link opens at this date and time." />
        <TextField label="Ends" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} InputLabelProps={{ shrink: true }} helperText="After this, the buy link shows Event ended. The launch time can be after this." />
        <TextField
          label="Launch date and time"
          type="datetime-local"
          value={form.launch_at}
          onChange={(e) => setForm({ ...form, launch_at: e.target.value })}
          InputLabelProps={{ shrink: true }}
          helperText="Launch stays closed until this time. After that, Launch now picks a new winner each time."
        />
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Top banner {form.banner ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0], "banner")} />
        </Button>
        {form.banner && <Box component="img" src={form.banner} alt="Banner" sx={{ width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 1.5 }} />}
        <TextField label="Banner link" value={form.banner_link} onChange={(e) => setForm({ ...form, banner_link: e.target.value })} placeholder="https://example.com" helperText="Opens when someone taps the banner on the booking page." />
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Image {form.image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0], "image")} />
        </Button>
        {form.image && <Box component="img" src={form.image} alt="Ticket" sx={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 1.5 }} />}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/tickets")}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save Ticket"}</Button>
        </Box>
      </Box>
    </Box>
  );
}

export function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const shown = orders.filter((order) => matchesCustomer(order, query));

  useEffect(() => {
    let mounted = true;
    adminPost("TICKET_DETAIL", { ticket_id: ticketId }).then((result) => {
      if (!mounted || result.status !== "200") return;
      setTicket(result.data?.ticket || null);
      setOrders(result.data?.orders || []);
    });
    return () => { mounted = false; };
  }, [ticketId]);

  const link = ticketLink(ticketId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/tickets")}>Back to Lucky Draw Ticket</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5, justifyItems: { xs: "stretch", sm: "start" } }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{headingCase(ticket?.title) || "Lucky Draw Ticket"}</Typography>
        <Typography sx={{ color: consoleColors.muted }}>{[ticketWindowLabel(ticket), `₹${formatAmount(ticket?.price)} each`, `up to ${ticket?.max_qty ?? "—"} coupons at a time`, `${ticket?.open_count || 0} link opens`, `${ticket?.sold || 0} purchased`, ticket?.ended === "1" ? "Expired" : ticket?.upcoming === "1" ? "Not started" : (ticket?.status === "0" ? "Disabled" : "Enabled")].filter(Boolean).join(" · ")}</Typography>
        {ticket?.image && <Box component="img" src={ticket.image} alt="" sx={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 2 }} />}
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 1.5 }}>
          <QRCodeCanvas value={link} size={180} />
        </Box>
        <Typography sx={{ fontSize: 13, wordBreak: "break-all" }}>{link}</Typography>
        <Button variant="contained" sx={{ ...primaryButtonSx, justifySelf: "start" }} onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}>Copy link</Button>
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 750, mt: 3, mb: 1 }}>Purchased Customers</Typography>
      <TicketSearchField value={query} onChange={setQuery} />
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, overflow: "auto", mt: 1.5 }}>
        {shown.length === 0 && <Typography sx={{ p: 2, color: consoleColors.muted }}>{query.trim() ? "No matching name or mobile." : "No payments yet."}</Typography>}
        {shown.map((order) => (
          <Box key={order.order_id} sx={{ px: 2, py: 1.2, borderTop: `1px solid ${consoleColors.line}`, display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{nameCase(order.customer_name)} · {order.customer_mobile}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{[order.customer_email, `${order.qty} coupons`, order.txnid ? `Transaction ${order.txnid}` : "", indiaDateTime(order.date_added)].filter(Boolean).join(" · ")}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 750 }}>₹{formatAmount(order.amount)} · {headingCase(order.status)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

async function orderCards(order, ticket) {
  return buildOrderTickets(order, order.title || ticket?.title, order.image || ticket?.image);
}

export function IssuedCustomerTable({ ticket, orders, emptyText = "No purchased customers yet." }) {
  const actions = (order) => (
    <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
      <Button size="small" variant="contained" sx={primaryButtonSx} onClick={async () => {
        const cards = await orderCards(order, ticket);
        cards.forEach((card, index) => setTimeout(() => saveTicketFile(card), index * 250));
      }}>Save</Button>
      <Button size="small" sx={{ textTransform: "none", color: "#15803d" }} onClick={async () => {
        const cards = await orderCards(order, ticket);
        const card = cards[0];
        if (!card) return;
        const text = `Lucky Draw Ticket\n${order.title || ticket?.title || ""}\n${order.customer_name || ""}\n${card.code}`;
        shareTicketFile(card, text).catch((error) => { if (error?.name !== "AbortError") toast.error("Could not share the ticket"); });
      }}>WhatsApp</Button>
    </Box>
  );

  if (orders.length === 0) {
    return (
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 2 }}>
        <Typography sx={{ color: consoleColors.muted }}>{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.2 }}>
        {orders.map((order) => (
          <Box key={order.order_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{nameCase(order.customer_name)}{order.is_winner === "1" ? " · Winner" : ""}</Typography>
            {order.title ? <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{headingCase(order.title)}</Typography> : null}
            <Typography sx={{ fontSize: 14, mt: 0.4, wordBreak: "break-word" }}>{order.customer_mobile}</Typography>
            <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.8 }}>Payment Transaction Id</Typography>
            <Typography sx={{ fontSize: 13.5, wordBreak: "break-all" }}>{order.txnid || "—"}</Typography>
            <Typography sx={{ fontSize: 14, mt: 0.8 }}>Qty {order.qty} · ₹{formatAmount(order.amount)}</Typography>
            {indiaDateTime(order.date_added) ? (
              <>
                <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.8 }}>Date and time</Typography>
                <Typography sx={{ fontSize: 13.5 }}>{indiaDateTime(order.date_added)}</Typography>
              </>
            ) : null}
            <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.8 }}>Codes</Typography>
            <Typography sx={{ fontSize: 13.5, wordBreak: "break-word" }}>{(order.codes || []).join(", ") || "—"}</Typography>
            <Box sx={{ mt: 1.2 }}>{actions(order)}</Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: { xs: "none", md: "block" }, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, overflow: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr 1.3fr 0.9fr 0.4fr 0.6fr 1fr 1.1fr", gap: 1, px: 2, py: 1.2, background: "#f8fafc", minWidth: 1100 }}>
          {["Name", "Mobile", "Payment Transaction Id", "Date and time", "Qty", "Amount", "Codes", "Ticket"].map((label) => (
            <Typography key={label} sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{label}</Typography>
          ))}
        </Box>
        {orders.map((order) => (
          <Box key={order.order_id} sx={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr 1.3fr 0.9fr 0.4fr 0.6fr 1fr 1.1fr", gap: 1, px: 2, py: 1.1, borderTop: `1px solid ${consoleColors.line}`, minWidth: 1100, alignItems: "center" }}>
            <Typography sx={{ fontSize: 13.5 }}>{nameCase(order.customer_name)}{order.is_winner === "1" ? " · Winner" : ""}</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{order.customer_mobile}</Typography>
            <Typography sx={{ fontSize: 13.5, wordBreak: "break-all" }}>{order.txnid || "—"}</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{indiaDateTime(order.date_added) || "—"}</Typography>
            <Typography sx={{ fontSize: 13.5 }}>{order.qty}</Typography>
            <Typography sx={{ fontSize: 13.5 }}>₹{formatAmount(order.amount)}</Typography>
            <Typography sx={{ fontSize: 13.5, wordBreak: "break-word" }}>{(order.codes || []).join(", ") || "—"}</Typography>
            {actions(order)}
          </Box>
        ))}
      </Box>
    </>
  );
}

export function TicketIssued() {
  const { ticketId } = useParams();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "winners" ? "winners" : "issued";
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [orders, setOrders] = useState([]);
  const [winners, setWinners] = useState([]);
  const [query, setQuery] = useState("");
  const shown = orders.filter((order) => matchesCustomer(order, query));

  useEffect(() => {
    let mounted = true;
    adminPost("TICKET_ISSUED", { ticket_id: ticketId }).then((result) => {
      if (!mounted) return;
      if (result.status !== "200") {
        toast.error(result?.data?.error || result?.status_message || "Could not load purchased customers");
        return;
      }
      setTicket(result.data?.ticket || null);
      setOrders(result.data?.orders || []);
      setWinners(result.data?.winners || []);
    });
    return () => { mounted = false; };
  }, [ticketId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/tickets")}>Back to Lucky Draw Ticket</Button>
        <Button sx={{ textTransform: "none" }} onClick={() => {
          if (!ticket?.list_token) return;
          if (view === "winners") {
            navigator.clipboard.writeText(winnersShareLink(ticket.list_token));
            toast.success("Winners link copied");
            return;
          }
          navigator.clipboard.writeText(issuedShareLink(ticket.list_token));
          toast.success("Issued list link copied");
        }}>{view === "winners" ? "Copy winners link" : "Copy share link"}</Button>
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 0.4 }}>{headingCase(ticket?.title) || "Lucky Draw Ticket"}</Typography>
      <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 2 }}>{issuedStatsLine(orders, ticket)}</Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {["issued", "winners"].map((item) => (
          <Button key={item} variant={view === item ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setParams(item === "issued" ? {} : { view: item })}>
            {item === "issued" ? "Issued customers" : "Winners"}
          </Button>
        ))}
      </Box>
      {view === "issued" ? (
        <>
          <Box sx={{ mb: 1.5 }}>
            <TicketSearchField value={query} onChange={setQuery} />
          </Box>
          <IssuedCustomerTable ticket={ticket} orders={shown} emptyText={query.trim() ? "No matching name or mobile." : "No purchased customers yet."} />
        </>
      ) : (
        <TicketWinnerTable winners={winners} />
      )}
    </Box>
  );
}

export function TicketWinnerTable({ winners }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, overflow: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.1fr 0.8fr", gap: 1, px: 2, py: 1.2, background: "#f8fafc", minWidth: 560 }}>
        {["Name", "Mobile", "Launch time", "Code"].map((label) => (
          <Typography key={label} sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{label}</Typography>
        ))}
      </Box>
      {(!winners || winners.length === 0) && <Typography sx={{ p: 2, color: consoleColors.muted }}>No winners yet.</Typography>}
      {(winners || []).map((row, index) => (
        <Box key={`${row.coupon_code || row.customer_mobile}-${row.winner_at || index}`} sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.1fr 0.8fr", gap: 1, px: 2, py: 1.1, borderTop: `1px solid ${consoleColors.line}`, minWidth: 560 }}>
          <Typography sx={{ fontSize: 13.5 }}>{nameCase(row.customer_name)}</Typography>
          <Typography sx={{ fontSize: 13.5 }}>{row.customer_mobile || "—"}</Typography>
          <Typography sx={{ fontSize: 13.5 }}>{indiaDateTime(row.winner_at || row.date_added) || "—"}</Typography>
          <Typography sx={{ fontSize: 13.5 }}>{row.coupon_code || "—"}</Typography>
        </Box>
      ))}
    </Box>
  );
}
